/**
 * Filter utility functions for dashboard-level filtering
 *
 * NOTE: These are pure functions without internal caching.
 * Memoization should be handled at the component level using useMemo.
 */

import type {
  Filter,
  DashboardFilter,
  DashboardFilterMapping,
  DashboardFilterMappingEntry,
  CubeMeta,
  GroupFilter,
  DashboardConfig,
  SimpleFilter
} from '../types.js'
import { ensureAnalysisConfig } from './configMigration.js'
import {
  transformFiltersForServer,
  validateFilterForCube,
  extractFilterMembers
} from '../shared/filters/index.js'

export { validateFilterForCube }

/**
 * Normalize a portlet filter mapping to entry objects.
 * Plain string entries (the legacy format) become { filterId } with no override.
 */
export function normalizeFilterMapping(
  mapping: DashboardFilterMapping | undefined
): DashboardFilterMappingEntry[] {
  if (!mapping) return []
  return mapping.map(entry =>
    typeof entry === 'string' ? { filterId: entry } : entry
  )
}

/**
 * Serialize mapping entries for storage.
 * Entries without a member override collapse back to plain strings so saved
 * configs only change shape when an override is actually used.
 */
export function serializeFilterMapping(
  entries: DashboardFilterMappingEntry[]
): DashboardFilterMapping {
  return entries.map(entry => (entry.member ? entry : entry.filterId))
}

/**
 * Check whether a mapping (either format) includes a given dashboard filter
 */
export function mappingIncludesFilter(
  mapping: DashboardFilterMapping | undefined,
  filterId: string
): boolean {
  if (!mapping) return false
  return mapping.some(entry =>
    typeof entry === 'string' ? entry === filterId : entry.filterId === filterId
  )
}

/**
 * Get the per-portlet member override for a dashboard filter, if any
 */
export function getMappingMemberOverride(
  mapping: DashboardFilterMapping | undefined,
  filterId: string
): string | undefined {
  if (!mapping) return undefined
  const entry = mapping.find(e =>
    typeof e === 'string' ? e === filterId : e.filterId === filterId
  )
  return typeof entry === 'object' ? entry.member : undefined
}

/**
 * Clone a filter with its member rewritten to the override field.
 * Only SimpleFilters can be rewritten; GroupFilters pass through unchanged.
 */
export function applyMemberOverride(filter: Filter, member?: string): Filter {
  if (!member) return filter
  if ('member' in filter && 'operator' in filter) {
    return { ...filter, member }
  }
  return filter
}

/**
 * Check if a filter should be included in the query (has valid values or doesn't require values)
 * @param filter - The filter to check
 * @returns true if the filter should be included, false otherwise
 */
export function shouldIncludeFilter(filter: Filter): boolean {
  // Handle SimpleFilter
  if ('member' in filter && 'operator' in filter) {
    const simpleFilter = filter as SimpleFilter

    // Operators that don't require values
    const noValueOperators = ['set', 'notSet', 'isEmpty', 'isNotEmpty']
    if (noValueOperators.includes(simpleFilter.operator)) {
      return true
    }

    // For inDateRange, check if dateRange is provided as alternative to values
    if (simpleFilter.operator === 'inDateRange' && simpleFilter.dateRange) {
      return true
    }

    // For other operators, check if values exist and are non-empty
    return !!(simpleFilter.values && simpleFilter.values.length > 0)
  }

  // Handle GroupFilter - recursively check nested filters
  if ('type' in filter && 'filters' in filter) {
    const groupFilter = filter as GroupFilter
    // Include group filter if at least one nested filter is valid
    const validFilters = groupFilter.filters.filter(f => shouldIncludeFilter(f))
    return validFilters.length > 0
  }

  return false
}

/**
 * Get dashboard filters that should be applied to a portlet based on its mapping configuration
 *
 * Mapping entries may carry a per-portlet member override, in which case the
 * filter is applied with its member rewritten to the override field (operator
 * and values stay shared across all portlets).
 *
 * @param dashboardFilters - All available dashboard filters
 * @param filterMapping - The portlet's filter mapping (filter IDs or entries with member overrides)
 * @returns Array of filters that should be applied to the portlet
 */
export function getApplicableDashboardFilters(
  dashboardFilters: DashboardFilter[] | undefined,
  filterMapping: DashboardFilterMapping | undefined
): Filter[] {
  if (!dashboardFilters || !dashboardFilters.length) {
    return []
  }

  // If no mapping is specified, no dashboard filters apply
  if (!filterMapping || !filterMapping.length) {
    return []
  }

  // Compute filters that are in the mapping AND have valid values,
  // rewriting the member where the mapping carries an override
  return dashboardFilters
    .filter(df => mappingIncludesFilter(filterMapping, df.id))
    .filter(df => shouldIncludeFilter(df.filter))
    .map(df => applyMemberOverride(df.filter, getMappingMemberOverride(filterMapping, df.id)))
}

/**
 * Filter format for merge operation:
 * - 'server': Returns {and: [...]} or {or: [...]} format (for API queries)
 * - 'client': Returns {type: 'and', filters: [...]} format (for UI components)
 */
export type FilterFormat = 'server' | 'client'

/**
 * Merge dashboard filters with portlet filters using AND logic
 * Dashboard filters are combined with portlet filters so both sets of filters apply
 *
 * @param dashboardFilters - Filters from dashboard-level configuration
 * @param portletFilters - Filters from portlet query
 * @param format - Output format: 'server' for API queries, 'client' for UI (default: 'server')
 * @returns Merged filter array with AND logic in the specified format
 */
export function mergeDashboardAndPortletFilters(
  dashboardFilters: Filter[],
  portletFilters: Filter[] | undefined,
  format: FilterFormat = 'server'
): Filter[] | undefined {
  // If no dashboard filters, return portlet filters as-is
  if (!dashboardFilters || dashboardFilters.length === 0) {
    return portletFilters
  }

  // If no portlet filters, return dashboard filters
  if (!portletFilters || portletFilters.length === 0) {
    return [...dashboardFilters]
  }

  // Both exist - need to merge with AND logic
  if (format === 'server') {
    // Server format: convert to {and: [...]} structure
    const allFilters = transformFiltersForServer([...dashboardFilters, ...portletFilters])
    return [{
      and: allFilters
    } as any]
  } else {
    // Client format: use {type: 'and', filters: [...]} structure
    const allFilters = [...dashboardFilters, ...portletFilters]
    return [{
      type: 'and',
      filters: allFilters
    } as GroupFilter]
  }
}

/**
 * @deprecated Use mergeDashboardAndPortletFilters(filters, portletFilters, 'client') instead
 */
export function mergeDashboardAndPortletFiltersClientFormat(
  dashboardFilters: Filter[],
  portletFilters: Filter[] | undefined
): Filter[] | undefined {
  return mergeDashboardAndPortletFilters(dashboardFilters, portletFilters, 'client')
}

/**
 * Validate that all dashboard filters in a portlet's mapping exist and are valid
 * @param dashboardFilters - All available dashboard filters
 * @param filterMapping - The portlet's filter mapping
 * @param cubeMeta - Cube metadata for validation
 * @returns Object with validation result and list of invalid filter IDs
 */
export function validatePortletFilterMapping(
  dashboardFilters: DashboardFilter[] | undefined,
  filterMapping: DashboardFilterMapping | undefined,
  cubeMeta: CubeMeta | null
): { isValid: boolean; invalidFilterIds: string[]; missingFilterIds: string[] } {
  if (!filterMapping || !filterMapping.length) {
    return { isValid: true, invalidFilterIds: [], missingFilterIds: [] }
  }

  const entries = normalizeFilterMapping(filterMapping)

  if (!dashboardFilters || !dashboardFilters.length) {
    // Mapping references filters that don't exist
    return {
      isValid: false,
      invalidFilterIds: [],
      missingFilterIds: entries.map(e => e.filterId)
    }
  }

  const invalidFilterIds: string[] = []
  const missingFilterIds: string[] = []

  entries.forEach(({ filterId, member }) => {
    const dashboardFilter = dashboardFilters.find(df => df.id === filterId)

    if (!dashboardFilter) {
      // Filter ID in mapping doesn't exist in dashboard filters
      missingFilterIds.push(filterId)
    } else if (member && !('member' in dashboardFilter.filter)) {
      // A member override on a group filter cannot be applied
      invalidFilterIds.push(filterId)
    } else {
      // Validate the effective filter (with any member override applied)
      const effectiveFilter = applyMemberOverride(dashboardFilter.filter, member)
      const isValid = validateFilterForCube(effectiveFilter, cubeMeta)
      if (!isValid) {
        invalidFilterIds.push(filterId)
      }
    }
  })

  return {
    isValid: invalidFilterIds.length === 0 && missingFilterIds.length === 0,
    invalidFilterIds,
    missingFilterIds
  }
}

/**
 * Extract all unique measures, dimensions, and timeDimensions used across all portlets in a dashboard
 * This helps create a filtered schema view showing only fields relevant to the dashboard
 * @param dashboardConfig - Dashboard configuration
 * @returns Object with unique measures, dimensions, and timeDimensions
 */
export function extractDashboardFields(
  dashboardConfig: DashboardConfig
): { measures: Set<string>; dimensions: Set<string>; timeDimensions: Set<string> } {
  const measures = new Set<string>()
  const dimensions = new Set<string>()
  const timeDimensions = new Set<string>()

  // Iterate through all portlets
  dashboardConfig.portlets.forEach(portlet => {
    try {
      // Get query from analysisConfig (migrating legacy format if needed)
      const normalizedPortlet = ensureAnalysisConfig(portlet)
      const query = normalizedPortlet.analysisConfig.query

      // Helper to extract fields from a CubeQuery
      const extractFromCubeQuery = (cubeQuery: any) => {
        if (cubeQuery.measures && Array.isArray(cubeQuery.measures)) {
          cubeQuery.measures.forEach((measure: string) => measures.add(measure))
        }
        if (cubeQuery.dimensions && Array.isArray(cubeQuery.dimensions)) {
          cubeQuery.dimensions.forEach((dimension: string) => dimensions.add(dimension))
        }
        if (cubeQuery.timeDimensions && Array.isArray(cubeQuery.timeDimensions)) {
          cubeQuery.timeDimensions.forEach((td: any) => {
            if (td.dimension) {
              timeDimensions.add(td.dimension)
            }
          })
        }
        if (cubeQuery.filters) {
          extractFilterMembers(cubeQuery.filters).forEach((field: string) => {
            dimensions.add(field)
          })
        }
      }

      // Handle different query types
      if ('funnel' in query) {
        // ServerFunnelQuery - extract time dimension from funnel config
        const funnelQuery = query as any
        if (funnelQuery.funnel?.timeDimension) {
          timeDimensions.add(funnelQuery.funnel.timeDimension)
        }
        // Could also extract binding key dimensions if needed
      } else if ('queries' in query) {
        // MultiQueryConfig - extract from all sub-queries
        const multiQuery = query as any
        multiQuery.queries.forEach((subQuery: any) => extractFromCubeQuery(subQuery))
      } else {
        // Single CubeQuery
        extractFromCubeQuery(query)
      }
    } catch (e) {
      // Skip portlets with invalid query
      console.warn('Failed to extract fields from portlet:', portlet.id, e)
    }
  })

  return { measures, dimensions, timeDimensions }
}

/**
 * Time dimension type from CubeQuery
 */
type TimeDimension = {
  dimension: string
  granularity?: string
  dateRange?: string[] | string
}

/**
 * Helper to get date range from a SimpleFilter (backward compatible)
 * Reads from both dateRange and values for compatibility
 * Handles both:
 * - Preset ranges: ["this quarter"], ["last 7 days"] (single string value)
 * - Custom ranges: ["2024-01-01", "2024-12-31"] (two date values)
 */
function getDateRangeFromFilter(filter: SimpleFilter): string[] | string | undefined {
  // Prefer dateRange for backward compatibility, fall back to values
  if (filter.dateRange) {
    return filter.dateRange
  }
  if (filter.values && filter.values.length > 0) {
    // Single value = preset like "this quarter", return as string
    // Multiple values = custom date range, return as array
    return filter.values.length === 1 ? filter.values[0] : filter.values
  }
  return undefined
}

/**
 * Apply universal time filters to a portlet's timeDimensions
 * Universal time filters apply their dateRange to ALL time dimensions in the portlet
 *
 * @param dashboardFilters - All dashboard filters
 * @param filterMapping - Filter IDs that apply to this portlet
 * @param portletTimeDimensions - The portlet's existing timeDimensions array
 * @returns Updated timeDimensions array with date ranges applied
 */
export function applyUniversalTimeFilters(
  dashboardFilters: DashboardFilter[] | undefined,
  filterMapping: DashboardFilterMapping | undefined,
  portletTimeDimensions: TimeDimension[] | undefined
): TimeDimension[] | undefined {
  // Return as-is if no time dimensions in portlet (skip silently)
  if (!portletTimeDimensions || portletTimeDimensions.length === 0) {
    return portletTimeDimensions
  }

  // If no mapping specified, no filters apply
  if (!filterMapping || filterMapping.length === 0) {
    return portletTimeDimensions
  }

  // Find applicable universal time filters that have valid date ranges
  const universalTimeFilters = dashboardFilters
    ?.filter(df => df.isUniversalTime && mappingIncludesFilter(filterMapping, df.id))
    ?.filter(df => {
      // Must be a SimpleFilter with a valid dateRange
      if (!('member' in df.filter)) return false
      const simpleFilter = df.filter as SimpleFilter
      const dateRange = getDateRangeFromFilter(simpleFilter)
      return dateRange !== undefined
    })

  if (!universalTimeFilters || universalTimeFilters.length === 0) {
    return portletTimeDimensions
  }

  // Use the first universal time filter's dateRange (typically only one)
  const timeFilter = universalTimeFilters[0]
  const simpleFilter = timeFilter.filter as SimpleFilter
  const dateRange = getDateRangeFromFilter(simpleFilter)

  // Apply dateRange to ALL time dimensions (dashboard wins - overrides portlet dateRange)
  return portletTimeDimensions.map(td => ({
    ...td,
    dateRange: dateRange
  }))
}
